import { CATEGORY_PURPOSE } from '@pet/shared';
import type { PaymentSource, SpendingPurpose, TaxCategoryOption } from '@pet/shared';
import { ReceiptApiError } from '../../lib/paymentClient';
import { isDeMinimisOverridden, type LineDraft } from '../../lib/buildPaymentPayload';
import { parseDollarsToCents, formatCentsAsDollars } from '../../lib/money';
import styles from '../AddSpending.module.css';

// Pieces shared by the Add Spending wizard's screens.

// .claude/rules/ux-wizard.md: the method literals in use, per account. Chips only, so spellings
// cannot drift; a method that fits neither list is left blank.
export const METHOD_OPTIONS: Record<PaymentSource, string[]> = {
  LLC_FUNDED: ['ACH', 'Zelle', 'Cash'],
  OWNER_FUNDED: ['Personal CC', 'Personal Check', 'Zelle', 'ACH', 'Cash'],
};

export const ACCOUNT_LABELS: Record<PaymentSource, string> = {
  LLC_FUNDED: 'LLC bank account',
  OWNER_FUNDED: 'An owner paid personally',
};

// A property line: LineDraft's property/category/flag/description, plus the "What was this for?"
// answer (wizard state only, never sent) and what the assignment screen types for it.
// amountCents on the draft is filled from the allocation at save. `descriptionEdited` is set by
// any typing in a description input; until then a line's description is derived (Build C: its
// receipt items' descriptions when items are tapped, else blank) and never overwrites an edit.
export interface SpendLine extends LineDraft {
  purpose: SpendingPurpose | '';
  amountText: string;
  percentText: string;
  descriptionEdited: boolean;
}

// AD-10 (amended 2026-09-23): what a line was for is asked before its category.
export const PURPOSE_OPTIONS: { value: SpendingPurpose; label: string; detail: string }[] = [
  { value: 'improvement', label: 'Improvement', detail: 'Renovating, upgrading, rebuilding' },
  { value: 'repair_or_upkeep', label: 'Repair or upkeep', detail: "Fixing or maintaining what's there" },
  { value: 'not_building_work', label: 'Not building work', detail: 'Fees, taxes, insurance, utilities, interest' },
];

// The categories an answer offers, in the API's order. A key the groups do not know, and
// `depreciation` ("not offered"), are offered under no answer.
export function categoriesFor(
  categories: TaxCategoryOption[],
  purpose: SpendingPurpose | ''
): TaxCategoryOption[] {
  if (purpose === '') return [];
  return categories.filter((c) => (CATEGORY_PURPOSE as Record<string, string>)[c.key] === purpose);
}

// The fixed note under the answers on a pre-service line answered Improvement (AD-10).
export const PRE_SERVICE_NOTE = 'Recorded as an improvement while the house is under construction.';

// Why a description is required, shown while it is blank.
export const DESCRIPTION_REQUIRED_REASON =
  'Please describe it: while the house is under construction, a line not recorded as an improvement needs a description.';

// The text before the first comma of the API's address ("2301 Sample Street"); the whole
// address when there is no comma.
export function shortPropertyLabel(address: string): string {
  const comma = address.indexOf(',');
  return comma === -1 ? address : address.slice(0, comma).trim();
}

export function todayISO(): string {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

export function mapPostError(err: unknown): string {
  if (err instanceof ReceiptApiError) {
    switch (err.status) {
      case 400:
        return "Something in the entry wasn't accepted. Please review and try again.";
      case 413:
        return 'That file is too large (15 MB max).';
      case 415:
        return "That file type isn't supported.";
      case 422:
        return "Couldn't read that image. Please try another photo.";
    }
  }
  return "We couldn't save this payment. Please try again — nothing was saved.";
}

export function ChipGroup<T extends string>({
  options,
  selected,
  onSelect,
  ariaLabel,
}: {
  options: { value: T; label: string; detail?: string }[];
  selected: T | '';
  onSelect: (value: T) => void;
  ariaLabel: string;
}) {
  return (
    <div className={styles.chipGroup} role="group" aria-label={ariaLabel}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={opt.value === selected ? styles.chipSelected : styles.chip}
          onClick={() => onSelect(opt.value)}
          aria-pressed={opt.value === selected}
        >
          {opt.label}
          {opt.detail && <span className={styles.chipDetail}>{opt.detail}</span>}
        </button>
      ))}
    </div>
  );
}

// Buffered dollar input: typing is kept as text, parsed to integer cents on every change, and
// normalised to 0.00 form on blur.
export function DollarInput({
  text,
  onText,
  ariaLabel,
}: {
  text: string;
  onText: (text: string) => void;
  ariaLabel: string;
}) {
  return (
    <input
      className={styles.amountInput}
      type="text"
      inputMode="decimal"
      placeholder="0.00"
      value={text}
      onChange={(e) => onText(e.target.value)}
      onBlur={() => {
        const cents = parseDollarsToCents(text);
        if (text.trim() === '') onText('');
        else if (cents !== null) onText(formatCentsAsDollars(cents));
      }}
      aria-label={ariaLabel}
    />
  );
}

export function NavRow({
  onBack,
  onNext,
  canNext,
  nextLabel = 'Next',
}: {
  onBack: () => void;
  onNext: () => void;
  canNext: boolean;
  nextLabel?: string;
}) {
  return (
    <div className={styles.navRow}>
      <button className={styles.secondaryButton} onClick={onBack}>
        Back
      </button>
      <button className={styles.primaryButton} disabled={!canNext} onClick={onNext}>
        {nextLabel}
      </button>
    </div>
  );
}

// Why Next or Save is disabled, in plain words. Renders nothing when nothing blocks.
export function BlockReasons({ reasons }: { reasons: string[] }) {
  if (reasons.length === 0) return null;
  return (
    <ul className={styles.reasonList} aria-label="Why you can't continue yet">
      {reasons.map((r) => (
        <li key={r}>{r}</li>
      ))}
    </ul>
  );
}

// "What was this for?" — the three answers, and the fixed note while a pre-service line is
// answered Improvement (AD-10, amended 2026-09-23).
export function PurposeControls({
  line,
  preService,
  onSelect,
}: {
  line: SpendLine;
  preService: boolean;
  onSelect: (purpose: SpendingPurpose) => void;
}) {
  return (
    <>
      <ChipGroup
        ariaLabel="What this was for"
        options={PURPOSE_OPTIONS}
        selected={line.purpose}
        onSelect={onSelect}
      />
      {preService && line.purpose === 'improvement' && (
        <p className={styles.overrideNote}>{PRE_SERVICE_NOTE}</p>
      )}
    </>
  );
}

// The "?" beside a treatment question: opens that question's live Guide section, verbatim (the
// wizard returns to the same step with its answers kept). Text from the Guide only (tax-data.md).
export function HelpButton({ question, onClick }: { question: string; onClick: () => void }) {
  return (
    <button type="button" className={styles.helpButton} aria-label={`Help: ${question}`} onClick={onClick}>
      ?
    </button>
  );
}

// The per-line small-purchase (de minimis) question. Prefilled from the category default by the
// caller; the note says whether the human has changed the usual answer. The label says what the
// flag records, and nothing about the amount (.claude/rules/ux.md).
export function SmallPurchaseControls({
  line,
  categories,
  onChange,
  onHelp,
}: {
  line: SpendLine;
  categories: TaxCategoryOption[];
  onChange: (line: SpendLine) => void;
  onHelp?: () => void;
}) {
  const dmChanged = isDeMinimisOverridden(line, categories);
  const question = 'Mark for the small-purchase treatment?';
  return (
    <>
      {onHelp ? (
        <div className={styles.questionRow}>
          <label className={styles.fieldLabel}>{question}</label>
          <HelpButton question={question} onClick={onHelp} />
        </div>
      ) : (
        <label className={styles.fieldLabel}>{question}</label>
      )}
      <ChipGroup
        ariaLabel="Small-purchase treatment"
        options={[
          { value: 'yes', label: 'Yes — mark it' },
          { value: 'no', label: 'No — leave unmarked' },
        ]}
        selected={line.deMinimisEligible === null ? '' : line.deMinimisEligible ? 'yes' : 'no'}
        onSelect={(value) => onChange({ ...line, deMinimisEligible: value === 'yes' })}
      />
      <p className={`${styles.overrideNote} ${dmChanged ? styles.overrideChanged : ''}`}>
        {dmChanged ? 'You changed this from the usual answer.' : 'Using the usual answer for this category.'}
      </p>
    </>
  );
}

export function LineFacts({
  line,
  categories,
}: {
  line: SpendLine;
  categories: TaxCategoryOption[];
}) {
  const dmOverridden = isDeMinimisOverridden(line, categories);
  return (
    <span className={styles.lineCardDetail}>
      {line.deMinimisEligible ? 'Marked for small-purchase treatment' : 'Not marked for small-purchase treatment'}
      {dmOverridden ? ' (changed)' : ''}
    </span>
  );
}
